import { Box, InputLabel, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { enGB, zhCN, zhTW } from "date-fns/locale";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Constant } from "../../Constant";
import { isEmpty, isNotEmpty, isObjectNotEmpty } from "../../Utils";
import CustomSelect from "../../inputComponent/CustomSelect/CustomSelect";
import { getForm } from "../../redux/selector";
import ErrorMsg from "../ErrorMsg/ErrorMsg";

export default function CustomDatePicker({
    label = "",
    control,
    name,
    type = "day",
    showError = true,
    required = true,
    autocomplete = false,
    tooltip,
    onChange = () => {},
    onBlur = () => {},
    ErrorMsgStyle = {},
    disabled = false,
    needValue = false,
    disableInput = false,
    ...restProps
}) {
    const autofillOffList = Constant.autoCompleteOffList;
    const { formId } = useSelector(getForm());
    const isAutofillOff = autofillOffList.includes(formId) || formId === "";

    if (restProps && !restProps.id) {
        if (name) restProps.id = name;
    }
    const [lang, setLang] = useState(enGB);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        switch (i18n.language) {
            case "tc":
                setLang(zhTW);
                break;
            case "sc":
                setLang(zhCN);
                break;
            case "en":
            default:
                setLang(enGB);
                break;
        }
    }, [i18n.language]);

    const toHKTimeStamp = (value, noTime = true) => {
        if (value) {
            const localTime = value;
            const day = localTime.getDate();
            const month = localTime.getMonth() + 1;
            const year = localTime.getFullYear();
            const hour = noTime ? 0 : localTime.getHours();
            const min = noTime ? 0 : localTime.getMinutes();
            const sec = noTime ? 0 : localTime.getSeconds();
            return moment.tz(
                day +
                    " " +
                    month +
                    " " +
                    year +
                    " " +
                    hour +
                    ":" +
                    min +
                    ":" +
                    sec,
                "DD MM YYYY hh:mm:ss",
                "Asia/Hong_Kong"
            );
        }
        return value;
    };

    const toDisplayTimeStamp = (value) => {
        if (value) {
            const hkTime = moment.tz(value, "Asia/Hong_Kong");
            const day = hkTime.format("DD");
            const month = hkTime.format("MM") - 1;
            const year = hkTime.format("YYYY");
            const hour = hkTime.format("HH");
            const min = hkTime.format("mm");
            const sec = hkTime.format("ss");
            return new Date(year, month, day, hour, min, sec);
        }
        return value;
    };

    // if label present by array, not string
    const handleMultiLabel = (labelList) => {
        return labelList.map((label) => {
            return (
                <>
                    {label}
                    <br />
                </>
            );
        });
    };

    //get date picker by type
    const getDatePickerByType = (type, field, fieldState) => {
        //field //是香港地區時間,及需傳送到server
        //For Display
        const displayField = toDisplayTimeStamp(field.value);
        const commonProps = {
            maxDate: new Date("2100/12/31"),
            showPopperArrow: false,
            onChange: (value) => {
                field.onChange(value);
                onChange(value);
            },
            onBlur: () => {
                field.onBlur();
                onBlur();
            },
            onChangeRaw: disableInput
                ? (e) => {
                      e.preventDefault();
                  }
                : () => {},

            selected: isObjectNotEmpty(field.value)
                ? moment(displayField).toDate()
                : displayField,
            useWeekdaysShort: i18n.language === "en",
        };
        const yearProps = {
            showPopperArrow: false,
            onChange: (value) => {
                if (isObjectNotEmpty(value)) {
                    const year = moment(value).year();
                    field.onChange(year);
                    onChange(year);
                } else {
                    field.onChange(value);
                    onChange(value);
                }
            },
            onBlur: () => {
                field.onBlur();
                onBlur();
            },
            selected: isObjectNotEmpty(field.value)
                ? moment(field.value).toDate()
                : field.value,
        };

        const maxYear = isNotEmpty(restProps.maxDate)
            ? moment(restProps.maxDate).year()
            : 9999;
        const minYear = isNotEmpty(restProps.minDate)
            ? moment(restProps.minDate).year()
            : 0;
        const getYearOption = () => {
            const currentYear = moment().toDate().getFullYear();
            const optionMinYear = isNotEmpty(restProps.minYearOption)
                ? restProps.minYearOption
                : minYear < 1900
                ? minYear
                : 1900;
            const optionMaxYear = maxYear !== 9999 ? maxYear : currentYear;
            const options = [];
            for (let index = optionMaxYear; index >= optionMinYear; index--) {
                options.push(index.toString());
            }
            return options;
        };
        const yearOption = getYearOption();
        switch (type) {
            case "day":
                return (
                    <>
                        <DatePicker
                            disabled={disabled}
                            {...commonProps}
                            // dateFormat="dd/MM/yyyy"
                            dateFormat={"dd/MM/yyyy"}
                            locale={lang}
                            placeholderText={
                                i18n.language === "en"
                                    ? "DD/MM/YYYY"
                                    : "日/月/年"
                            }
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            {...restProps}
                            autoComplete={isAutofillOff ? "off" : "on"}
                            onChange={(value) => {
                                const hkTime = toHKTimeStamp(value);
                                field.onChange(hkTime);
                                onChange(hkTime);
                            }}
                        />
                    </>
                );
            case "month":
                return (
                    <DatePicker
                        disabled={disabled}
                        {...commonProps}
                        // dateFormat="MMMM yyyy"
                        dateFormat={"MM/yyyy"}
                        placeholderText={
                            i18n.language === "en" ? "MM/YYYY" : "月份/年份"
                        }
                        showMonthYearPicker
                        showFullMonthYearPicker
                        locale={lang}
                        {...restProps}
                        autoComplete={isAutofillOff ? "off" : "on"}
                    />
                );
            case "year":
                return (
                    <>
                        <Autocomplete
                            style={{ width: "80%" }}
                            disableClearable
                            value={
                                isNotEmpty(field.value)
                                    ? moment(field.value).format(
                                          isNotEmpty(restProps.dateFormat)
                                              ? restProps.dateFormat
                                              : "yyyy"
                                      )
                                    : field.value
                            }
                            onChange={(event, newValue) => {
                                field.onChange(
                                    isEmpty(newValue) ? "" : moment(newValue)
                                );
                                onChange(event);
                            }}
                            disabled={disabled}
                            options={yearOption}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={restProps?.placeholderText}
                                />
                            )}
                            autoComplete={isAutofillOff ? "off" : "on"}
                            // getOptionDisabled={(option) =>
                            //     option > maxYear || option < minYear
                            // }
                            openOnFocus={true}
                            {...restProps}
                        />
                    </>
                );
            case "pureMonth":
                return (
                    <CustomSelect
                        control={control}
                        name={name}
                        options={t("common.monthOption", {
                            returnObjects: true,
                        })}
                        showError={false}
                        {...commonProps}
                        {...restProps}
                        needValue={needValue}
                    />
                );
            case "pureYear":
                return (
                    <DatePicker
                        {...yearProps}
                        showYearPicker
                        dateFormat="yyyy"
                        placeholderText={
                            i18n.language === "en" ? "YYYY" : "年份"
                        }
                        locale={lang}
                        {...restProps}
                        autoComplete={isAutofillOff ? "off" : "on"}
                        yearItemNumber={12}
                    />
                );
            case "dateTime":
                return (
                    <DatePicker
                        {...commonProps}
                        timeInputLabel={`${t("common.time")}:`}
                        // dateFormat="MM/dd/yyyy h:mm aa"
                        dateFormat="MM/dd/yyyy h:mm aa"
                        placeholderText={
                            i18n.language === "en"
                                ? "DD/MM/YYYY 12:00 AM"
                                : "日/月/年 12:00 上午"
                        }
                        showTimeInput
                        locale={lang}
                        {...restProps}
                        autoComplete={isAutofillOff ? "off" : "on"}
                        onChange={(value) => {
                            const hkTime = toHKTimeStamp(value, false);
                            field.onChange(hkTime);
                            onChange(hkTime);
                        }}
                    />
                );
            default:
                break;
        }
    };

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <Box>
                    {!isEmpty(label) && (
                        <Box mb={4}>
                            <InputLabel
                                disabled={disabled}
                                error={isObjectNotEmpty(fieldState.error)}
                                shrink={true}
                                htmlFor={restProps.id}
                            >
                                <Box>
                                    {label}
                                    {t("common.colon")}
                                    {required && (
                                        <Box
                                            component="span"
                                            className="danger"
                                        >
                                            *
                                        </Box>
                                    )}
                                    {tooltip}
                                </Box>
                            </InputLabel>
                        </Box>
                    )}
                    <Box>{getDatePickerByType(type, field, fieldState)}</Box>
                    {showError && (
                        <ErrorMsg
                            message={fieldState?.error?.message}
                            style={ErrorMsgStyle}
                        />
                    )}
                </Box>
            )}
        />
    );
}
